import React, { useState, useEffect, useRef } from 'react';
import ARView from 'components/modules/ARView'
import * as Tone from 'tone';
import * as PIXI from 'pixi.js';
import BoxDefinitions from 'components/utils/boxDefinitions';
import classnames from 'classnames';

// icons
import Pause from 'svg/pause';
import Play from 'svg/play';
import Lock from 'svg/lock';
import LockOpen from 'svg/lock-open';

const Detect = (props) => {
  // Refs to save state
  const canvasRef = useRef(null);
  const deskewViewRef = useRef(null);
  const overlayRef = useRef(null);
  const checkCanvasRef = useRef(null);
  const sliderCheckCanvasRef = useRef(null);
  const packCheckCanvasRef = useRef(null);
  const simpleRef = useRef(null);
  const noDrawMode = useRef(false);
  const pixi = useRef(null);
  const graphics = useRef(null);
  const currentMeasure = useRef(0);
  const puffs = useRef([]);
  const counter = useRef(0);
  const puffScores = useRef(null);
  const soundPacksScore = useRef(-1);
  const sliderScore = useRef(0);
  const cornerWhiteAvgArr = useRef([]);
  const avgWhiteSample = useRef(0);
  const currentOctave = useRef(3);
  const liveBoxRunningCount = useRef(0);
  const soloState = useRef(false);
  const soloBoxes = useRef([false, false, false]);
  const seenBoxes = useRef([false, false, false]);
  // State
  const [isLandscape, setIsLandscape] = useState(false);
  const [innerPaddingBottom, setInnerPaddingBottom] = useState(0);
  const [lockMessageVisible, setLockMessageVisible] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [isSolo, setIsSolo] = useState(false);
  const [totalSeenBoxes, setTotalSeenBoxes] = useState(0);
  // variables
  const puffsWhiteBalance = 1.3;
  const controlsWhiteBalance = 1.3;
  const sliderWhiteBalance = 1.3;
  let simpleArr = [];
  const boxes = [BoxDefinitions.creamy, BoxDefinitions.crunchy, BoxDefinitions.puffy];
  // let lockImage = null;
  const measureCount = 16;
  const emptyArr = new Array(measureCount).fill(new Array(5).fill(0));
  let lastMarkers = null;
  let lastDetectTime = 0;
  const orangeHex = 0xf37027;
  const purpleHex = 0x9927f3;
  const boxPackDimensions = {
    width: 344,
    height: 50
  };
  // We skew the box slider 1.5x on the y axis,
  // because that gives a better representation
  // of where the puff actually is
  const boxSliderDimensions = {
    width: 50,
    height: 165
  };

  /**
   * Instantiate the puffs,
   * and set up inner padding to have video fill screen
   */
  useEffect(() => {
    for (let i = 0; i < measureCount; i++) {
      puffs.current[i] = [];
    }
    const checkOrientation = () => {
      if (window.matchMedia("(orientation: landscape)").matches) {
        setIsLandscape(true);
      } else {
        setIsLandscape(false);
      }
    }
    const setScale = () => {
      // Set the padding bottom to get get fluid fixed aspect ratio containers
      let height = document.documentElement.clientHeight;
      let width = document.documentElement.clientWidth;
      setInnerPaddingBottom(height/width);
    }
    window.addEventListener('resize', setScale);
    window.addEventListener('resize', checkOrientation);
    setScale();
    checkOrientation();
    // PIXI
    pixi.current = new PIXI.Application({
      resizeTo: window,
      view: overlayRef.current,
      transparent: true,
      antialias: true
    });
    graphics.current = new PIXI.Graphics();
    pixi.current.stage.addChild(graphics.current);
    // Set debug mode if we have `?debug` in our URL
    setIsDebugMode(window.location.search.indexOf('debug') !== -1);
    return () => {
      window.removeEventListener('resize', setScale);
      window.removeEventListener('resize', checkOrientation);
    }
    // eslint-disable-next-line
  }, []);

  /**
   * If we need to degrade the experience
   */
  useEffect(() => {
    if (props.degrade === true) {
      noDrawMode.current = true;
    }
  }, [props.degrade]);

  /**
   * Update the current measure based on updates
   * from our main music controller callback
   */
  useEffect(() => {
    currentMeasure.current = props.currentMeasure;
  }, [props.currentMeasure]);

  /**
   * Our main AR callback function,
   * this gets the data from our AR view
   */
  const arCallback = (markers, image, w, h) => {
    counter.current++;
    canvasRef.current.height = deskewViewRef.current.height = overlayRef.current.height = h;
    canvasRef.current.width = deskewViewRef.current.width = overlayRef.current.width = w;
    pixi.current.renderer.resize(w, h);
    // Add the image data to the canvas
    canvasRef.current.getContext('2d').putImageData(image, 0, 0);
    // Clear the graphics
    graphics.current.clear();
    // Parse the markers
    parseMarkers(markers);
    // Update the notes of the live boxes every measure in the music,
    // so ppl can move puffs around and edit live
    if (props.liveBox.current &&
      typeof props.liveBox.current.idNum === 'number' &&
      !props.lockedBoxes.current[props.liveBox.current.idNum]) {
      props.controller.updateEntirePattern(props.liveBox.current.idNum, puffs.current);
    }
    // Make sure we're not playing old notes from a previous live box that wasn't locked...
    props.lockedBoxes.current.forEach((box, index) => {
      // If our box isn't locked,
      // and we're not currently playing the live one
      if (!box &&
        props.liveBox.current.idNum !== index) {
        props.controller.updateEntirePattern(index, emptyArr);
      }
    });
  };

  /**
   * Play/pause the AR animation on screen,
   * as well as the music
   */
  const playPause = () => {
    // if we're playing already
    if (props.isPlaying) {
      props.setIsPlaying(false);
      props.controller.stopTransport();
    } else { // otherwise, we should play
      props.setIsPlaying(true);
      Tone.start();
      props.controller.startTransport();
    }
  };

  /**
   * Lock or unlock the current box
   */
  const lockUnlock = () => {
    if (props.lockState.current === 'unlocked') {
      props.lockState.current = 'locked';
      props.setIsLocked(true);
      props.lockedBoxes.current[props.liveBox.current.idNum] = props.liveBox.current;
    } else {
      props.lockState.current = 'unlocked';
      props.setIsLocked(false);
      props.lockedBoxes.current[props.liveBox.current.idNum] = false;
    }
    props.setHasLockedBox(!props.lockedBoxes.current.every(v => !v));
  };

  /**
   * Solo or "solo off" the current box
   */
  const soloUnsolo = () => {
    if (soloState.current === false) {
      soloState.current = true;
      setIsSolo(true);
      soloBoxes.current[props.liveBox.current.idNum] = props.liveBox.current;
      for (let i = 0; i < soloBoxes.current.length; i++) {
        props.controller.muteTrack(i);
      }
      props.controller.unmuteTrack(props.liveBox.current.idNum);
    } else {
      soloState.current = false;
      setIsSolo(false);
      for (let i = 0; i < soloBoxes.current.length; i++) {
        soloBoxes.current[i] = false;
        props.controller.unmuteTrack(i);
      }
    }
  };

  /**
   * Handle any updates when we find a new box
   * @param  {object} box [box object]
   */
  const newBox = (box) => {
    //make sure the check canvases are the correct size
    checkCanvasRef.current.width = Math.floor(box.gridWidth / 4);
    checkCanvasRef.current.height = Math.floor(box.gridHeight / 4);
    //toggle lock
    if (!props.lockedBoxes.current[box.idNum] && props.lockState.current === 'locked') {
      //this isn't a locked box but we're in lock mode -- toggle to unlocked
      lockUnlock();
    } else if (props.lockedBoxes.current[box.idNum] && props.lockState.current === 'unlocked') {
      //this is a locked box and we're unlocked -- toggle to locked
      lockUnlock();
    }
    if (soloBoxes.current[box.idNum] && soloState.current === false) {
      soloUnsolo();
    } else if (!soloBoxes.current[box.idNum] && soloState.current === true) {
      soloUnsolo();
    }
  };

  /**
   * Parse markers
   * @param  {array} markers [the array of markers]
   */
  const parseMarkers = (markers) => {
    //for each box with a set of markers
    for (let i = 0; i < boxes.length; i++) {
      var boxMarkers = boxes[i].markers;
      var markerCount = boxMarkers.length;
      var found = [];
      //go through the current markers and see if they match a box
      for (let j = 0; j < markers.length; j++) {
        if (boxMarkers.indexOf(markers[j].idMatrix) > -1 && found.indexOf(markers[j].idMatrix) === -1) {
          found.push(markers[j].idMatrix);
        }
      }
      //we've found all of the markers that define a box
      if (found.length === markerCount) {
        //order the markers
        let corners = [];
        for (let k = 0; k < boxMarkers.length; k++) {
          for (let l = 0; l < markers.length; l++) {
            if (markers[l].idMatrix === boxMarkers[k]) {
              corners[k] = markers[l]
            }
          }
        }
        //reorder the vertices for consistency
        for (let k = 0; k < corners.length; k++) {
          for (let l = 0; l < corners[k].dirMatrix; l++) {
            let x = corners[k].vertex.pop();
            corners[k].vertex.unshift(x);
          }
        }
        parseBox(boxes[i], corners);
        if (props.liveBox.current && props.liveBox.current.id !== boxes[i].id) {
          props.liveBox.current = boxes[i];
          newBox(boxes[i]);
        }
        props.liveBox.current = boxes[i];
        props.setHasLiveBox(true);
        seenBoxes.current[i] = true;
        setTotalSeenBoxes(seenBoxes.current.reduce((arr, cur) => arr + cur));
        props.setHasPreviouslyShownOverlay(true);
        lastMarkers = corners;
        lastDetectTime = new Date().getTime();
        // Play if we're getting a live box for the first time
        if (!liveBoxRunningCount.current) {
          playPause();
        }
        liveBoxRunningCount.current++;
        return;
      }
    }
    //we didn't find a valid set of markers, see if we have an old set that isn't too old
    let d = new Date().getTime();
    if (d - lastDetectTime < 1000) {
      parseBox(props.liveBox.current, lastMarkers);
    } else {
      props.liveBox.current = {};
      props.setHasLiveBox(false);
      if (soloState.current === true) {
        soloUnsolo();
      }
      // draw the markers
      for (let j = 0; j < markers.length; j++) {
        if (markers[j].idMatrix > -1 && !noDrawMode.current) {
          graphics.current.beginFill(purpleHex);
          graphics.current.drawCircle(markers[j].pos[0], markers[j].pos[1], 10);
          graphics.current.endFill();
        }
      }
    }
  };

  /**
   * Parse the box, given the corners
   * @param  {object} box     [box to parse]
   * @param  {array} corners  [array of corners information]
   */
  const parseBox = (box, corners) => {
    if (props.lockedBoxes.current[box.idNum]) {
      setLockMessageVisible(true);
      showLockedMessage(corners);
    } else {
      setLockMessageVisible(false);
      findWhite(corners);
      if (Object.keys(box).length !== 0 && box.constructor === Object) {
        drawGrid(corners, box);
      }
    }
  };

  /**
   * Get average corner color
   *
   * Given an area, get the average color of its corners
   * @param  {imageData} imageData [ctx image data]
   * @param  {number}    width     [the width of our canvas]
   * @param  {array}     corner    [an array of corner points]
   * @return {number}              [the average]
   */
  const getAverageCornerColor = (imageData, width, corner) => {
    let tl = (Math.floor(corner.vertex[0][0]) + (Math.floor(corner.vertex[0][1]) * width)) * 4;
    let tr = (Math.floor(corner.vertex[1][0]) + (Math.floor(corner.vertex[1][1]) * width)) * 4;
    let bl = (Math.floor(corner.vertex[2][0]) + (Math.floor(corner.vertex[2][1]) * width)) * 4;
    let br = (Math.floor(corner.vertex[3][0]) + (Math.floor(corner.vertex[3][1]) * width)) * 4;
    let avgTL = (imageData.data[tl] + imageData.data[tl+1] + imageData.data[tl+2])/3;
    let avgTR = (imageData.data[tr] + imageData.data[tr+1] + imageData.data[tr+2])/3;
    let avgBL = (imageData.data[bl] + imageData.data[bl+1] + imageData.data[bl+2])/3;
    let avgBR = (imageData.data[br] + imageData.data[br+1] + imageData.data[br+2])/3;
    return (avgTL + avgTR + avgBL + avgBR)/4;
  };

  /**
   * Find White
   *
   * This attempts to find the whites on the box,
   * and use those to color correct for different lighting scenarios
   */
  const findWhite = (corners) => {
    let ctx = canvasRef.current.getContext('2d');
    let imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
    let width = canvasRef.current.width;
    let cornerWhiteSamples = [];
    for (let i = 0; i < corners.length; i++) {
      cornerWhiteSamples[i] = getAverageCornerColor(imageData, width, corners[i]);
    }
    const avg = (arr) => {
      return arr.reduce((a, b) => a + b)/arr.length;
    }
    cornerWhiteAvgArr.current.unshift(avg(cornerWhiteSamples));
    cornerWhiteAvgArr.current = cornerWhiteAvgArr.current.slice(0, 10);
    avgWhiteSample.current = cornerWhiteAvgArr.current.reduce((a, b) => a + b)/cornerWhiteAvgArr.current.length;
  };

  const listToMatrix = (list, elementsPerSubArray) => {
    return list.reduce((rows, key, index) => (index % elementsPerSubArray === 0 ? rows.push([key]) : rows[rows.length-1].push(key)) && rows, []);
  };

  /**
   * Visit island function:
   * check around an area to see what
   * surroundings look like
   */
  const visitIsland = (i, j, depth = 0) => {
    // probably some opportunity here to limit the size of the blob that we find.
    // throw safety check to make sure it's not too deep of a call
    depth++;
    if (depth < 16) {
      try {
        simpleArr[i][j] = 0;
        if (simpleArr[i - 1] && simpleArr[i - 1][j] === 1) visitIsland(i - 1, j, depth);
        if (simpleArr[i + 1] && simpleArr[i + 1][j] === 1) visitIsland(i + 1, j, depth);
        if (simpleArr[i][j + 1] === 1) visitIsland(i, j + 1, depth);
        if (simpleArr[i][j - 1] === 1) visitIsland(i, j - 1, depth);
      } catch (e) {
        console.warn(e);
      }
    }
  };

  /**
   * Check diagonals,
   * see if we find anything that needs
   * to be thrown to visitIsland
   * to see if a puff is there
   * @param  {number} size [size of the diagonal]
   * @param  {number} i    [x position]
   * @param  {number} j    [y position]
   *
   * @return {bool}        [whether we found a diagonal]
   */
  const checkDiag = (size, i, j) => {
    for (let k = 0; k < size; k++) {
      if (i + k >= simpleArr.length) { return false; }
      if (j + k >= simpleArr[i + k].length) { return false; }
      if (simpleArr[i + k][j + k] === 0) { return false; }
    }
    //we found a diagonal, zero eveything touching this out
    visitIsland(i, j);
    return true;
  };

  /**
   * Find and check the puffs on the box
   * @param  {Object} box [the box]
   */
  const checkPuffs = (box) => {
    let width = Math.floor(box.gridWidth / 4);
    let height = Math.floor(box.gridHeight / 4);
    let ctx = checkCanvasRef.current.getContext('2d');
    //eliminate the edges
    ctx.beginPath();
    ctx.lineWidth = 5;
    ctx.strokeStyle = '#ffffff';
    ctx.moveTo(0, 0);
    ctx.lineTo(width, 0);
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.stroke();
    ctx.lineWidth = 1;
    //also ignore any masked areas
    if (box.masks) {
      for (let i = 0; i < box.masks.length; i++) {
        ctx.beginPath();
        ctx.fillStyle = '#ffffff';
        ctx.moveTo(0, height * box.masks[i][0]);
        ctx.lineTo(width, height * box.masks[i][0]);
        ctx.lineTo(width, height * box.masks[i][0] + height * box.masks[i][1]);
        ctx.lineTo(0, height * box.masks[i][0] + height * box.masks[i][1]);
        ctx.closePath();
        ctx.fill();
      }
    }
    let imageData = ctx.getImageData(0, 0, width, height);
    let flat = [];
    //basic thresholding
    let c = 0;
    for (let i = 0; i < imageData.data.length; i += 4) {
      let whiteSample = avgWhiteSample.current * puffsWhiteBalance;
      //basically if pixel is white-ish
      if (imageData.data[i] > whiteSample &&
        imageData.data[i + 1] > whiteSample &&
        imageData.data[i + 2] > whiteSample) {
        imageData.data[i] = 255;
        imageData.data[i + 1] = 255;
        imageData.data[i + 2] = 255;
        flat[c] = 0;
      } else {
        imageData.data[i] = 0;
        imageData.data[i + 1] = 0;
        imageData.data[i + 2] = 0;
        flat[c] = 1;
      }
      c++;
    }
    // debug only
    ctx.putImageData(imageData, 0, 0);
    simpleArr = listToMatrix(flat, width);
    let diags = [];
    let diagSize = 6;
    for (let i = 0; i < simpleArr.length; i++) {
      for (let j = 0; j < simpleArr[i].length; j++) {
        //this is an 'on' pixel
        if (simpleArr[i][j] === 1) {
          if (checkDiag(diagSize, i, j)) {
            diags.push([j, i]);
          }
        }
      }
    }
    let nscores = [];
    let scaleX = width / box.gridCols;
    //zero out our data
    for (let j = 0; j < box.gridCols; j++) {
      nscores[j] = [];
      for (let i = 0; i < box.rows.length; i++) {
        nscores[j][i] = 0;
      }
    }
    for (let i = 0; i < diags.length; i++) {
      //debug only
      ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.beginPath();
      ctx.arc(diags[i][0] + (1 * diagSize), diags[i][1] + diagSize, 6, 0, 2 * Math.PI);
      ctx.fill();
      let col = Math.floor((diags[i][0] + (1 * diagSize)) / scaleX);
      let row = 0;
      let startY = box.rows[0][1];
      //rows may have space between them so we can't just calculate it
      for (let j = 0; j < box.rows.length; j++) {
        if ((diags[i][1] + diagSize) * 4 >= box.rows[j][1] - startY) {
          row = j;
        }
      }
      if (nscores[col]) {
        nscores[col][row] = 1;
      }
    }
    return nscores;
  };

  /**
   * Detect the sound pack controls on the box
   * @param  {Object} box [the box]
   */
  const checkSoundPacks = (box) => {
    //todo BOX
    let ctx = packCheckCanvasRef.current.getContext('2d');
    let imageData = ctx.getImageData(0, 0, boxPackDimensions.width, boxPackDimensions.height);
    //basic thresholding
    for (let i = 0; i < imageData.data.length; i += 4) {
      let whiteSample = avgWhiteSample.current * controlsWhiteBalance;
      //basically if pixel is white-ish
      if (imageData.data[i] > whiteSample &&
        imageData.data[i + 1] > whiteSample &&
        imageData.data[i + 2] > whiteSample) {
        imageData.data[i] = 255;
        imageData.data[i + 1] = 255;
        imageData.data[i + 2] = 255;
      } else {
        imageData.data[i] = 0;
        imageData.data[i + 1] = 0;
        imageData.data[i + 2] = 0;
      }
    }
    //debug
    ctx.putImageData(imageData, 0, 0);
    // Calculate the offset,
    // by tracking where we start to see white
    let offsetObj = {
      score: 0,
      x: 0
    };
    for (let x = 0; x < 160; x+= 4) {
      for (let y = (box.packHeight * 4)/2 - 40; y < (box.packHeight * 4)/2 + 40; y += 4) {
        let p = x + (y * boxPackDimensions.width);
        if (imageData.data[p] === 0) {
          offsetObj.score = 0;
        } else {
          offsetObj.score++;
        }
      }
      if (offsetObj.score >= 16) {
        offsetObj.x = x;
        break;
      }
    }
    //see if one of the controls is checked
    let scores = [];
    // loop through all the packs
    for (let i = 0; i < box.packs.length; i++) {
      scores[i] = 0;
      let s = (box.packs[i][0] - box.packs[0][0]) * 4 + offsetObj.x;
      for (let k = 0; k < box.packWidth * 4; k += 4) {
        // only check the vertical middle 0.5 of the pack
        for (let m = (Math.floor(box.packHeight * 0.15) * 4); m < (Math.floor(box.packHeight * 0.85) * 4); m += 4) {
          let p = s + k + (m * boxPackDimensions.width);
          if (imageData.data[p] === 0) {
            scores[i] += 1 / (box.packWidth * box.packHeight * 0.7);
          }
          if (k === 0 || m === (Math.floor(box.packHeight * 0.15) * 4) || k === (box.packWidth - 1) * 4 || m === (Math.floor(box.packHeight * 0.85) * 4) - 4) {
            imageData.data[p] = 255;
            imageData.data[p + 1] = 0;
            imageData.data[p + 2] = 0;
          }
        }
      }
      // If we saw more than two thirds of it in black
      if (scores[i] > .66) {
        return i;
      }
    }
    //debug
    ctx.putImageData(imageData, 0, 0);
    return -1;
  };

  /**
   * Find and check the sliders on the box
   * @param  {array} slider [the slider array]
   */
  const checkSlider = (slider) => {
    //todo BOX
    let ctx = sliderCheckCanvasRef.current.getContext('2d');
    let imageData = ctx.getImageData(0, 0, boxSliderDimensions.width, boxSliderDimensions.height);
    //basic thresholding
    for (let i = 0; i < imageData.data.length; i += 4) {
      let whiteSample = avgWhiteSample.current * sliderWhiteBalance;
      //basically if pixel is white-ish
      if (imageData.data[i] > whiteSample &&
        imageData.data[i + 1] > whiteSample &&
        imageData.data[i + 2] > whiteSample) {
        imageData.data[i] = 255;
        imageData.data[i + 1] = 255;
        imageData.data[i + 2] = 255;
      } else {
        imageData.data[i] = 0;
        imageData.data[i + 1] = 0;
        imageData.data[i + 2] = 0;
      }
    }
    //debug
    ctx.putImageData(imageData, 0, 0);
    // go from top to bottom and see where we're at,
    // but start 1/4 toward middle and 1/8 from top
    let maxRows = 0;
    let currentRows = 0;
    let rowStart = 0;
    let startX = Math.floor(boxSliderDimensions.width/4);
    let endX = boxSliderDimensions.width - Math.floor(boxSliderDimensions.width/4);
    let startY = Math.floor(boxSliderDimensions.height/8);
    let endY = boxSliderDimensions.height - Math.floor(boxSliderDimensions.height/8);
    for (let i = startY; i < endY; i++) {
      var solid = 0;
      // Check only the middle half of the slider
      for (let j = startX; j < endX; j++) {
        if (imageData.data[(i * 4 * boxSliderDimensions.width) + (j * 4)] === 0) {
          solid++;
        } else {
          break;
        }
      }
      // Since we're checking just the middle of the slider,
      // we want to know if we're picking up more than half
      // of that area as solid color
      if (solid >= endX - startX) {
        currentRows++;
        if (currentRows > maxRows) {
          rowStart = i - currentRows;
          maxRows = currentRows;
        }
      } else {
        currentRows = 0;
      }
    }
    var percent = 0;
    if (maxRows > 15) {
      percent = (rowStart / boxSliderDimensions.height);
    } else {
      return 0;
    }
    return 1 - percent;
  };

  /**
   * Show the locked box graphics (a purple line around box)
   * @param  {array} corners [corners information]
   */
  const showLockedMessage = (corners) => {
    //outside corners of the markers
    let outsideUL = corners[0].vertex[0];
    let outsideUR = corners[1].vertex[1];
    let outsideBR = corners[2].vertex[2];
    let outsideBL = corners[3].vertex[3];
    //draw the border of everything
    graphics.current.lineStyle(2, purpleHex, 1);
    graphics.current.moveTo(outsideUL[0], outsideUL[1]);
    graphics.current.lineTo(outsideUR[0], outsideUR[1]);
    graphics.current.lineTo(outsideBR[0], outsideBR[1]);
    graphics.current.lineTo(outsideBL[0], outsideBL[1]);
    graphics.current.lineTo(outsideUL[0], outsideUL[1]);
    graphics.current.moveTo(0, 0);
  };

  /**
   * Draw the grid
   * @param  {Array} corners          [array of corner information]
   * @param  {Object} box             [box object]
   */
  const drawGrid = (corners, box) => {
    var totalWidth = box.totalWidth;
    var totalHeight = box.totalHeight;
    var gridWidth = box.gridWidth;
    var gridHeight = box.gridHeight;
    var rows = box.rows;
    var rowHeight = box.squareHeight;
    var boxWidth = box.squareWidth;
    var gridCols = box.gridCols;
    var sliders = box.sliders;
    var packs = box.packs;
    var packWidth = box.packWidth;
    var packHeight = box.packHeight;
    let m0corner = corners[0].vertex[3]; // bottom left corner of UL marker
    let m1corner = corners[1].vertex[2]; // botton right corner of UR marker
    let m2corner = corners[2].vertex[1]; // upper right corner of BR marker
    let m3corner = corners[3].vertex[0]; // upper left corner of BL marker
    //outside corners of the markers
    let outsideUL = corners[0].vertex[0];
    let outsideUR = corners[1].vertex[1];
    let outsideBR = corners[2].vertex[2];
    let outsideBL = corners[3].vertex[3];
    //draw the border of everything
    graphics.current.lineStyle(2, purpleHex, 1);
    graphics.current.moveTo(outsideUL[0], outsideUL[1]);
    graphics.current.lineTo(outsideUR[0], outsideUR[1]);
    graphics.current.lineTo(outsideBR[0], outsideBR[1]);
    graphics.current.lineTo(outsideBL[0], outsideBL[1]);
    graphics.current.lineTo(outsideUL[0], outsideUL[1]);
    graphics.current.moveTo(0, 0);
    let topWidth = Math.sqrt(Math.pow((m0corner[0] - m1corner[0]), 2) + Math.pow((m0corner[1] - m1corner[1]), 2));
    let bottomWidth = Math.sqrt(Math.pow((m3corner[0] - m2corner[0]), 2) + Math.pow((m3corner[1] - m2corner[1]), 2));
    let leftHeight = Math.sqrt(Math.pow((m0corner[0] - m3corner[0]), 2) + Math.pow((m0corner[1] - m3corner[1]), 2));
    let rightHeight = Math.sqrt(Math.pow((m1corner[0] - m2corner[0]), 2) + Math.pow((m1corner[1] - m2corner[1]), 2));
    //control toggle
    if (box.packs) {
      //find the box containing all controls
      //left
      let p1 = shortenLineDistance(m0corner[0], m0corner[1], m1corner[0], m1corner[1], (topWidth / totalWidth) * packs[0][0]);
      let p2 = shortenLineDistance(m3corner[0], m3corner[1], m2corner[0], m2corner[1], (bottomWidth / totalWidth) * packs[0][0]);
      let leftBorder = [p1, p2];
      //right
      p1 = shortenLineDistance(m0corner[0], m0corner[1], m1corner[0], m1corner[1], (topWidth / totalWidth) * (packs[packs.length - 1][0] + packWidth));
      p2 = shortenLineDistance(m3corner[0], m3corner[1], m2corner[0], m2corner[1], (bottomWidth / totalWidth) * (packs[packs.length - 1][0] + packWidth));
      let rightBorder = [p1, p2];
      //top
      p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * packs[0][1]);
      p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * packs[0][1]);
      let topBorder = [p1, p2];
      //bottom
      p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * (packs[packs.length - 1][1] + packHeight));
      p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * (packs[packs.length - 1][1] + packHeight));
      let bottomBorder = [p1, p2];
      let cornerUL = lineIntersect(topBorder, leftBorder);
      let cornerUR = lineIntersect(topBorder, rightBorder);
      let cornerBL = lineIntersect(bottomBorder, leftBorder);
      let cornerBR = lineIntersect(bottomBorder, rightBorder);
      let topPackWidth = Math.sqrt(Math.pow((cornerUL.x - cornerUR.x), 2) + Math.pow((cornerUL.y - cornerUR.y), 2));
      let bottomPackWidth = Math.sqrt(Math.pow((cornerBL.x - cornerBR.x), 2) + Math.pow((cornerBL.y - cornerBR.y), 2));
      let packCorners = [cornerUL, cornerUR, cornerBR, cornerBL];
      deskew(packCorners, packCheckCanvasRef.current.getContext('2d'), 0, 0, boxPackDimensions.width, boxPackDimensions.height);
      soundPacksScore.current = checkSoundPacks(box);
      if (soundPacksScore.current > -1) {
        // If we have a pack control,
        // update the livebox preset
        // unless the live box is locked
        if (props.liveBox.current &&
          typeof props.liveBox.current.idNum === 'number' &&
          !props.lockedBoxes.current[props.liveBox.current.idNum]) {
          props.controller.setPresetById(soundPacksScore.current, props.liveBox.current.idNum);
        }
        //draw a circle if we found a puff on a control
        let totalPackWidth = packs[packs.length - 1][0] - packs[0][0] + box.packWidth;
        let p1 = shortenLineDistance(cornerUL.x, cornerUL.y, cornerUR.x, cornerUR.y, (topPackWidth / totalPackWidth) * (packs[soundPacksScore.current][0] - packs[0][0]));
        let p2 = shortenLineDistance(cornerBL.x, cornerBL.y, cornerBR.x, cornerBR.y, (bottomPackWidth / totalPackWidth) * (packs[soundPacksScore.current][0] - packs[0][0]));
        let p3 = shortenLineDistance(cornerUL.x, cornerUL.y, cornerUR.x, cornerUR.y, (topPackWidth / totalPackWidth) * (packs[soundPacksScore.current][0] - packs[0][0]) + (topPackWidth / totalPackWidth) * box.packWidth);
        let p4 = shortenLineDistance(cornerBL.x, cornerBL.y, cornerBR.x, cornerBR.y, (bottomPackWidth / totalPackWidth) * (packs[soundPacksScore.current][0] - packs[0][0]) + (bottomPackWidth / totalPackWidth) * box.packWidth);
        // let leftLine = [p1, p2];
        // let rightLine = [p3, p4];
        let ullr = [p1, p4];
        let llur = [p3, p2];
        let cen = lineIntersect(ullr, llur);
        if (!noDrawMode.current) {
          graphics.current.beginFill(purpleHex, 0.6);
          graphics.current.lineStyle(0);
          graphics.current.drawCircle(cen.x, cen.y, 12);
          graphics.current.endFill();
        }
      }
    }
    //find the sliders
    for (let i = 0; i < sliders.length; i++) {
      //left
      let p1 = shortenLineDistance(m0corner[0], m0corner[1], m1corner[0], m1corner[1], (topWidth / totalWidth) * sliders[i][0]);
      let p2 = shortenLineDistance(m3corner[0], m3corner[1], m2corner[0], m2corner[1], (bottomWidth / totalWidth) * sliders[i][0]);
      let leftBorder = [p1, p2];
      //right
      p1 = shortenLineDistance(m0corner[0], m0corner[1], m1corner[0], m1corner[1], (topWidth / totalWidth) * (sliders[i][2] + sliders[i][0]));
      p2 = shortenLineDistance(m3corner[0], m3corner[1], m2corner[0], m2corner[1], (bottomWidth / totalWidth) * (sliders[i][2] + sliders[i][0]));
      let rightBorder = [p1, p2];
      //top
      p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * sliders[i][1]);
      p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * sliders[i][1]);
      let topBorder = [p1, p2];
      //bottom
      p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * (sliders[i][1] + sliders[i][3]));
      p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * (sliders[i][1] + sliders[i][3]));
      let bottomBorder = [p1, p2];
      let cornerUL = lineIntersect(topBorder, leftBorder);
      let cornerUR = lineIntersect(topBorder, rightBorder);
      let cornerBL = lineIntersect(bottomBorder, leftBorder);
      let cornerBR = lineIntersect(bottomBorder, rightBorder);
      let gridCorners = [cornerUL, cornerUR, cornerBR, cornerBL];
      deskew(gridCorners, sliderCheckCanvasRef.current.getContext('2d'), 0, 0, boxSliderDimensions.width, boxSliderDimensions.height);
      sliderScore.current = checkSlider(sliders[i]);
      if (sliderScore.current > 0) {
        currentOctave.current = Math.floor(sliderScore.current * 6);
        // If we have an octave change
        // update the live box
        // unless that box is locked
        if (props.liveBox.current &&
          typeof props.liveBox.current.idNum === 'number' &&
          !props.lockedBoxes.current[props.liveBox.current.idNum]) {
          props.controller.setOctave(currentOctave.current - 3, props.liveBox.current.idNum);
        }
        p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * (sliders[i][1] + (sliders[i][3] * (1 - sliderScore.current))));
        p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * (sliders[i][1] + (sliders[i][3] * (1 - sliderScore.current))));
        topBorder = [p1, p2];
        cornerUL = lineIntersect(topBorder, leftBorder);
        cornerUR = lineIntersect(topBorder, rightBorder);
        if (!noDrawMode.current) {
          graphics.current.beginFill(purpleHex, 0.6);
          graphics.current.lineStyle(0);
          graphics.current.moveTo(cornerUL.x, cornerUL.y);
          graphics.current.lineTo(cornerUR.x, cornerUR.y);
          graphics.current.lineTo(cornerBR.x, cornerBR.y);
          graphics.current.lineTo(cornerBL.x, cornerBL.y);
          graphics.current.endFill();
          graphics.current.moveTo(0, 0);
        }
      } else {
        currentOctave.current = 3;
      }
    }
    //find the actual grid
    //left
    let p1 = shortenLineDistance(m0corner[0], m0corner[1], m1corner[0], m1corner[1], (topWidth / totalWidth) * rows[0][0]);
    let p2 = shortenLineDistance(m3corner[0], m3corner[1], m2corner[0], m2corner[1], (bottomWidth / totalWidth) * rows[0][0]);
    let leftBorder = [p1, p2];
    //right
    p1 = shortenLineDistance(m0corner[0], m0corner[1], m1corner[0], m1corner[1], (topWidth / totalWidth) * ((boxWidth * gridCols) + rows[0][0]));
    p2 = shortenLineDistance(m3corner[0], m3corner[1], m2corner[0], m2corner[1], (bottomWidth / totalWidth) * ((boxWidth * gridCols) + rows[0][0]));
    let rightBorder = [p1, p2];
    //top
    p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * rows[0][1]);
    p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * rows[0][1]);
    let topBorder = [p1, p2];
    //bottom
    p1 = shortenLineDistance(m0corner[0], m0corner[1], m3corner[0], m3corner[1], (leftHeight / totalHeight) * (rows[rows.length - 1][1] + rowHeight));
    p2 = shortenLineDistance(m1corner[0], m1corner[1], m2corner[0], m2corner[1], (rightHeight / totalHeight) * (rows[rows.length - 1][1] + rowHeight));
    let bottomBorder = [p1, p2];
    let cornerUL = lineIntersect(topBorder, leftBorder);
    let cornerUR = lineIntersect(topBorder, rightBorder);
    let cornerBL = lineIntersect(bottomBorder, leftBorder);
    let cornerBR = lineIntersect(bottomBorder, rightBorder);
    let topGridWidth = Math.sqrt(Math.pow((cornerUL.x - cornerUR.x), 2) + Math.pow((cornerUL.y - cornerUR.y), 2));
    let bottomGridWidth = Math.sqrt(Math.pow((cornerBL.x - cornerBR.x), 2) + Math.pow((cornerBL.y - cornerBR.y), 2));
    let leftGridHeight = Math.sqrt(Math.pow((cornerUL.x - cornerBL.x), 2) + Math.pow((cornerUL.y - cornerBL.y), 2));
    let rightGridHeight = Math.sqrt(Math.pow((cornerUR.x - cornerBR.x), 2) + Math.pow((cornerUR.y - cornerBR.y), 2));
    let gridCorners = [cornerUL, cornerUR, cornerBR, cornerBL];
    deskew(gridCorners, checkCanvasRef.current.getContext('2d'), 0, 0, Math.floor(gridWidth / 4), Math.floor(gridHeight / 4));
    // go through and draw the boxes + the current measure + the puffs
    puffScores.current = checkPuffs(box);
    if (puffScores.current) {
      let startY = rows[0][1];
      for (let i = 0; i < rows.length; i++) {
        let p1 = shortenLineDistance(cornerUL.x, cornerUL.y, cornerBL.x, cornerBL.y, (leftGridHeight / gridHeight) * (rows[i][1] - startY));
        let p2 = shortenLineDistance(cornerUR.x, cornerUR.y, cornerBR.x, cornerBR.y, (rightGridHeight / gridHeight) * (rows[i][1] - startY));
        let p3 = shortenLineDistance(cornerUL.x, cornerUL.y, cornerBL.x, cornerBL.y, (leftGridHeight / gridHeight) * (rows[i][1] + rowHeight - startY));
        let p4 = shortenLineDistance(cornerUR.x, cornerUR.y, cornerBR.x, cornerBR.y, (rightGridHeight / gridHeight) * (rows[i][1] + rowHeight - startY));
        let upperLine = [p1, p2];
        let lowerLine = [p3, p4];
        for (let j = 0; j < gridCols; j++) {
          let p1 = shortenLineDistance(cornerUL.x, cornerUL.y, cornerUR.x, cornerUR.y, (topGridWidth / gridWidth) * (j * boxWidth));
          let p2 = shortenLineDistance(cornerBL.x, cornerBL.y, cornerBR.x, cornerBR.y, (bottomGridWidth / gridWidth) * (j * boxWidth));
          let p3 = shortenLineDistance(cornerUL.x, cornerUL.y, cornerUR.x, cornerUR.y, (topGridWidth / gridWidth) * ((j + 1) * boxWidth));
          let p4 = shortenLineDistance(cornerBL.x, cornerBL.y, cornerBR.x, cornerBR.y, (bottomGridWidth / gridWidth) * ((j + 1) * boxWidth));
          let leftLine = [p1, p2];
          let rightLine = [p3, p4];
          let ul = lineIntersect(upperLine, leftLine);
          let ur = lineIntersect(upperLine, rightLine);
          let bl = lineIntersect(lowerLine, leftLine);
          let br = lineIntersect(lowerLine, rightLine);
          //highlight the current measure if this is the right column
          if (j === currentMeasure.current
            && !noDrawMode.current) {
            graphics.current.beginFill(purpleHex, 0.6);
            graphics.current.lineStyle(0);
            graphics.current.moveTo(ul.x, ul.y);
            graphics.current.lineTo(ur.x, ur.y);
            graphics.current.lineTo(br.x, br.y);
            graphics.current.lineTo(bl.x, bl.y);
            graphics.current.endFill();
            graphics.current.moveTo(0, 0);
          }
          //highlight a box with a puff in it
          if (j < gridCols) {
            if (puffScores.current[j][i]) {
              if (!noDrawMode.current) {
                if (j === currentMeasure.current) {
                  graphics.current.beginFill(orangeHex, 0.8);
                } else {
                  graphics.current.beginFill(purpleHex, 0.6);
                }
                graphics.current.lineStyle(0);
                graphics.current.moveTo(ul.x, ul.y);
                graphics.current.lineTo(ur.x, ur.y);
                graphics.current.lineTo(br.x, br.y);
                graphics.current.lineTo(bl.x, bl.y);
                graphics.current.endFill();
                graphics.current.moveTo(0, 0);
              }
              puffs.current[j][i] = 1;
            } else {
              puffs.current[j][i] = 0;
            }
          }
        }
      }
    }
  };

  /**
   * Deskew the box
   * @param  {array} corners    [array of corners information]
   * @param  {ctx}   tmpCanvas [temp canvas element context to deskew to]
   * @param  {number} xPos      [x position]
   * @param  {number} yPos      [y position]
   * @param  {number} width     [width]
   * @param  {number} height    [height]
   */
  const deskew = (corners, tmpCanvas, xPos, yPos, width, height) => {
    var anchors = {
      TL: { x: corners[0].x, y: corners[0].y },
      TR: { x: corners[1].x, y: corners[1].y },
      BR: { x: corners[2].x, y: corners[2].y },
      BL: { x: corners[3].x, y: corners[3].y },
    }
    var unwarped = {
      TL: { x: xPos, y: yPos },
      TR: { x: width, y: yPos },
      BR: { x: width, y: height },
      BL: { x: xPos, y: height },
    }
    unwarp(anchors, unwarped, tmpCanvas, canvasRef.current);
  };

  /**
   * Unwarp a box
   * @param  {object} anchors      [object of actual corners]
   * @param  {object} unwarped     [object of natural corners]
   * @param  {ctx}    context      [temp canvas context]
   * @param  {canvas} sourceCanvas [canvas we're getting info from]
   */
  const unwarp = (anchors, unwarped, context, sourceCanvas) => {
    // clear the destination canvas
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
    // unwarp the bottom-left triangle of the warped polygon
    mapTriangle(context,
      anchors.TL, anchors.BR, anchors.BL,
      unwarped.TL, unwarped.BR, unwarped.BL,
      sourceCanvas
    );
    // unwarp the top-right triangle of the warped polygon
    mapTriangle(context,
      anchors.TL, anchors.TR, anchors.BR,
      unwarped.TL, unwarped.TR, unwarped.BR,
      sourceCanvas
    );
  };

  /**
   * Map Triangles
   * @param  {ctx}    ctx          [canvas context]
   * @param  {object} p0           [anchor x y position]
   * @param  {object} p1           [anchor x y position]
   * @param  {object} p2           [anchor x y position]
   * @param  {object} p_0          [anchor x y position]
   * @param  {object} p_1          [anchor x y position]
   * @param  {object} p_2          [anchor x y position]
   * @param  {ctx}    sourceCanvas [source context]
   */
  const mapTriangle = (ctx, p0, p1, p2, p_0, p_1, p_2, sourceCanvas) => {
    // break out the individual triangles x's & y's
    var x0 = p_0.x, y0 = p_0.y;
    var x1 = p_1.x, y1 = p_1.y;
    var x2 = p_2.x, y2 = p_2.y;
    var u0 = p0.x, v0 = p0.y;
    var u1 = p1.x, v1 = p1.y;
    var u2 = p2.x, v2 = p2.y;
    // save the unclipped & untransformed destination canvas
    ctx.save();
    // clip the destination canvas to the unwarped destination triangle
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.closePath();
    ctx.clip();
    // Compute matrix transform
    var delta = u0 * v1 + v0 * u2 + u1 * v2 - v1 * u2 - v0 * u1 - u0 * v2;
    var delta_a = x0 * v1 + v0 * x2 + x1 * v2 - v1 * x2 - v0 * x1 - x0 * v2;
    var delta_b = u0 * x1 + x0 * u2 + u1 * x2 - x1 * u2 - x0 * u1 - u0 * x2;
    var delta_c = u0 * v1 * x2 + v0 * x1 * u2 + x0 * u1 * v2 - x0 * v1 * u2 - v0 * u1 * x2 - u0 * x1 * v2;
    var delta_d = y0 * v1 + v0 * y2 + y1 * v2 - v1 * y2 - v0 * y1 - y0 * v2;
    var delta_e = u0 * y1 + y0 * u2 + u1 * y2 - y1 * u2 - y0 * u1 - u0 * y2;
    var delta_f = u0 * v1 * y2 + v0 * y1 * u2 + y0 * u1 * v2 - y0 * v1 * u2 - v0 * u1 * y2 - u0 * y1 * v2;
    // Draw the transformed image
    ctx.transform(
      delta_a / delta, delta_d / delta,
      delta_b / delta, delta_e / delta,
      delta_c / delta, delta_f / delta
    );
    // draw the transformed source image to the destination canvas
    ctx.drawImage(sourceCanvas, 0, 0);
    // restore the context to it's unclipped untransformed state
    ctx.restore();
  };

  /**
   * Given two points, return point X distance between them
   * @param  {number} fromX      [start point x]
   * @param  {number} fromY      [start point y]
   * @param  {number} toX        [end point x]
   * @param  {number} toY        [end point y]
   * @param  {number} pxDistance [distance between from and to points]
   *
   * @return {object}            [point X distance between from and to points]
   */
  const shortenLineDistance = (fromX, fromY, toX, toY, pxDistance) => {
    var xlen = toX - fromX;
    var ylen = toY - fromY;
    // Determine hypotenuse length
    var hlen = Math.sqrt(xlen*xlen + ylen*ylen);
    var ratio = pxDistance / hlen;
    var smallerXLen = xlen * ratio;
    var smallerYLen = ylen * ratio;
    return { y: fromY + smallerYLen, x: fromX + smallerXLen };
  };

  /**
   * Find the point that two lines intersect
   * @param  {array} a  [line one info]
   * @param  {array} b  [line two info]
   *
   * @return {object}   [x and y position of intersection]
   */
  const lineIntersect = (a, b) => {
    a.m = (a[0].y - a[1].y) / (a[0].x - a[1].x);  // slope of line 1
    b.m = (b[0].y - b[1].y) / (b[0].x - b[1].x);  // slope of line 2
    return {
      x: (a.m * a[0].x - b.m * b[0].x + b[0].y - a[0].y) / (a.m - b.m),
      y: (a.m * b.m * (b[0].x - a[0].x) + b.m * a[0].y - a.m * b[0].y) / (b.m - a.m)
    };
  };

  return (
    <article className='Detect'>
      {/*Canvas checks for detection – all hidden*/}
      <canvas className={classnames('Detect__check-canvas', {
        show: isDebugMode
      })} ref={checkCanvasRef} width='232' height='72' />
      <canvas className={classnames('Detect__slider-check-canvas', {
        show: isDebugMode
      })} ref={sliderCheckCanvasRef} width={boxSliderDimensions.width} height={boxSliderDimensions.height} />
      <canvas className={classnames('Detect__pack-check-canvas', {
        show: isDebugMode
      })} ref={packCheckCanvasRef} width={boxPackDimensions.width} height={boxPackDimensions.height} />
      {/*Locked message*/}
      {(props.hasLiveBox && lockMessageVisible && !props.showViz) && (
        <section className='Detect__lock-message'>
          <div className='button'>
            <Lock />
          </div>
          <div className='button'>
            <h3 className='Detect__lock-message-text'>Pattern Locked</h3>
          </div>
        </section>
      )}
      {/*Play/Pause and the Lock button*/}
      {!props.showViz && (
        <div className={classnames('Actions', {
          show: props.hasLiveBox || props.hasLockedBox
        })}>
          {(totalSeenBoxes > 1 && props.hasLiveBox) && (
            <ul className='Actions__side-list'>
              <li className='Actions__side-list-item show'>
                <button
                  className={classnames('button Actions__list-button Actions__list-button-solo', {
                    active: isSolo
                  })}
                  onClick={soloUnsolo}
                >{isSolo ? 'Solo Off' : 'Solo'}</button>
              </li>
            </ul>
          )}
          <ul className='Actions__list'>
            <li className='Actions__list-item show'>
              <button
                title='Play/Pause'
                className='button Actions__list-button Actions__list-button-play'
                onClick={playPause}
              >{props.isPlaying ? (
                <Pause className='Actions__icon Actions__icon-pause' title='Pause' />
              ) : (
                <Play className='Actions__icon Actions__icon-play' title='Play' />
              )}</button>
            </li>
            {!props.degrade && (
              <li className={classnames('Actions__list-item', {
                show: props.hasLiveBox
              })}>
                <button
                  className={classnames('button Actions__list-button Actions__list-button-lock', {
                    locked: props.isLocked
                  })}
                  onClick={lockUnlock}
                >
                  <p className='Actions__list-button-lock__description'>{props.isLocked ? 'Locked' : 'Unlocked'}</p>
                  <div className='Actions__list-button-lock__inner'>
                    {props.isLocked ? (
                      <Lock title='Unlock' />
                    ) : (
                      <LockOpen title='Lock' />
                    )}
                  </div>
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
      {/*Musical instrument ref – used to play music*/}
      <div ref={simpleRef}></div>
      {/*Camera, notices, logo*/}
      <div className='Detect__frame'>
        <div className='Detect__frame--wrapper'>
          <div className='Detect__frame--inner' style={{paddingBottom: `${innerPaddingBottom * 100}%`}}>
            <canvas className='Detect__overlay' ref={overlayRef} />
            <canvas className='Detect__camera-view' ref={canvasRef} />
          </div>
        </div>
      </div>
      {/*Deskewing, used for calculations*/}
      <canvas className='Detect__deskew-view' ref={deskewViewRef} />
      {/*AR Stuff*/}
      {(isLandscape || props.hasVideo) && (
        <div className={classnames({'has-live-box': props.hasLiveBox})}>
          <ARView
            dataCallback={arCallback}
            setHasVideo={props.setHasVideo}
            degrade={props.degrade}
            showDownload={props.showDownload}
          />
        </div>
      )}
    </article>
  );
}

export default Detect;
